<template>
  <v-card variant="outlined" class="telegram-templates-panel mt-4">
    <div class="d-flex align-center ga-3 pa-3 flex-wrap">
      <v-icon size="20">mdi-file-document-multiple-outline</v-icon>
      <div>
        <div class="text-subtitle-2 font-weight-medium">
          {{ $t("ncrm.integrations.telegram.templates.title") }}
        </div>
        <div class="text-caption text-medium-emphasis">
          {{ $t("ncrm.integrations.telegram.templates.subtitle") }}
        </div>
      </div>
      <v-spacer />
      <v-btn color="primary" size="small" @click="openCreate">
        <v-icon start>mdi-plus</v-icon>
        {{ $t("ncrm.integrations.telegram.templates.add") }}
      </v-btn>
    </div>
    <v-divider />

    <v-data-table
      :headers="headers"
      :items="templates"
      :loading="loading"
      density="comfortable"
      class="elevation-0"
      item-key="id"
    >
      <template #item.body="{ item }">
        <span class="telegram-template-body-snippet">{{ snippet(item.body) }}</span>
      </template>
      <template #item.parse_mode="{ item }">
        <v-chip size="x-small">{{ item.parse_mode }}</v-chip>
      </template>
      <template #item.actions="{ item }">
        <v-btn size="small" variant="text" @click="openEdit(item)">
          <v-icon size="18">mdi-pencil</v-icon>
        </v-btn>
        <v-btn size="small" variant="text" color="error" @click="openDelete(item)">
          <v-icon size="18">mdi-delete-outline</v-icon>
        </v-btn>
      </template>
      <template #no-data>
        <v-alert type="info" class="ma-4" density="compact">
          {{ $t("ncrm.integrations.telegram.templates.empty") }}
        </v-alert>
      </template>
    </v-data-table>

    <v-dialog v-model="formDialog.show" max-width="640" persistent>
      <v-card>
        <v-card-title>
          {{
            formDialog.mode === "create"
              ? $t("ncrm.integrations.telegram.templates.dialog.create.title")
              : $t("ncrm.integrations.telegram.templates.dialog.edit.title")
          }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-text-field
            v-model="formDialog.data.name"
            :label="$t('ncrm.integrations.telegram.templates.fields.name')"
            :rules="[required]"
            required
          />
          <v-text-field
            v-model="formDialog.data.category"
            :label="$t('ncrm.integrations.telegram.templates.fields.category')"
          />
          <v-select
            v-model="formDialog.data.parseMode"
            :items="parseModeOptions"
            :label="$t('ncrm.integrations.telegram.templates.fields.parseMode')"
          />
          <v-textarea
            v-model="formDialog.data.body"
            :label="$t('ncrm.integrations.telegram.templates.fields.body')"
            :hint="$t('ncrm.integrations.telegram.templates.fields.bodyHint')"
            rows="6"
            auto-grow
            :rules="[required]"
            required
            persistent-hint
          />
          <v-textarea
            v-model="formDialog.data.description"
            :label="$t('ncrm.integrations.telegram.templates.fields.description')"
            rows="2"
            auto-grow
          />
          <v-alert v-if="formDialog.error" type="error" density="compact" class="mt-2">
            {{ formDialog.error }}
          </v-alert>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="formDialog.saving" @click="closeForm">
            {{ $t("ncrm.integrations.telegram.templates.dialog.actions.cancel") }}
          </v-btn>
          <v-btn color="primary" :loading="formDialog.saving" @click="submit">
            {{ $t("ncrm.integrations.telegram.templates.dialog.actions.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog.show" max-width="480" persistent>
      <v-card>
        <v-card-title>
          {{ $t("ncrm.integrations.telegram.templates.dialog.delete.title") }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <p class="mb-3">
            {{
              $t("ncrm.integrations.telegram.templates.dialog.delete.confirmText", {
                name: deleteDialog.row?.name || "",
              })
            }}
          </p>
          <v-text-field
            v-model="deleteDialog.confirm"
            :label="$t('ncrm.integrations.telegram.templates.dialog.delete.confirmType')"
            autocomplete="off"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="deleteDialog.deleting" @click="closeDelete">
            {{ $t("ncrm.integrations.telegram.templates.dialog.actions.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            :loading="deleteDialog.deleting"
            :disabled="deleteDialog.confirm !== 'DELETE'"
            @click="confirmDelete"
          >
            {{ $t("ncrm.integrations.telegram.templates.dialog.actions.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from "axios";

const buildEmptyForm = () => ({
  id: null,
  name: "",
  category: "",
  body: "",
  parseMode: "plain",
  description: "",
});

export default {
  name: "TelegramTemplatesPanel",
  emits: ["notify"],
  data() {
    return {
      loading: false,
      templates: [],
      formDialog: { show: false, mode: "create", saving: false, error: "", data: buildEmptyForm() },
      deleteDialog: { show: false, row: null, confirm: "", deleting: false },
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t("ncrm.integrations.telegram.templates.headers.name"), key: "name" },
        { title: this.$t("ncrm.integrations.telegram.templates.headers.category"), key: "category" },
        { title: this.$t("ncrm.integrations.telegram.templates.headers.parseMode"), key: "parse_mode" },
        { title: this.$t("ncrm.integrations.telegram.templates.headers.body"), key: "body" },
        {
          title: this.$t("ncrm.integrations.telegram.templates.headers.actions"),
          key: "actions",
          sortable: false,
          align: "end",
        },
      ];
    },
    parseModeOptions() {
      return [
        { title: "Plain text", value: "plain" },
        { title: "MarkdownV2", value: "MarkdownV2" },
        { title: "HTML", value: "HTML" },
      ];
    },
  },
  mounted() {
    this.fetchTemplates();
  },
  methods: {
    required(v) {
      return !!String(v || "").trim() || this.$t("ncrm.integrations.telegram.templates.requiredField");
    },
    snippet(body) {
      const s = String(body || "").replace(/\s+/g, " ").trim();
      return s.length > 80 ? `${s.slice(0, 80)}…` : s;
    },
    extractError(error, fallback) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        this.$t(fallback)
      );
    },
    async fetchTemplates() {
      this.loading = true;
      try {
        const { data } = await axios.get("/integrations/telegram/templates");
        this.templates = Array.isArray(data?.templates) ? data.templates : [];
      } catch (error) {
        this.$emit("notify", {
          message: this.extractError(error, "ncrm.integrations.telegram.templates.errors.load"),
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.formDialog = { show: true, mode: "create", saving: false, error: "", data: buildEmptyForm() };
    },
    openEdit(row) {
      this.formDialog = {
        show: true,
        mode: "edit",
        saving: false,
        error: "",
        data: {
          id: row.id,
          name: row.name || "",
          category: row.category || "",
          body: row.body || "",
          parseMode: row.parse_mode || "plain",
          description: row.description || "",
        },
      };
    },
    closeForm() {
      this.formDialog.show = false;
    },
    async submit() {
      this.formDialog.error = "";
      const d = this.formDialog.data;
      if (!String(d.name).trim() || !String(d.body).trim()) {
        this.formDialog.error = this.$t("ncrm.integrations.telegram.templates.requiredField");
        return;
      }
      this.formDialog.saving = true;
      try {
        const payload = {
          name: d.name.trim(),
          category: d.category?.trim() || null,
          body: d.body,
          parseMode: d.parseMode || "plain",
          description: d.description?.trim() || null,
        };
        if (this.formDialog.mode === "create") {
          await axios.post("/integrations/telegram/templates", payload);
          this.$emit("notify", {
            message: this.$t("ncrm.integrations.telegram.templates.snackbar.created"),
            color: "success",
          });
        } else {
          await axios.patch(`/integrations/telegram/templates/${d.id}`, payload);
          this.$emit("notify", {
            message: this.$t("ncrm.integrations.telegram.templates.snackbar.updated"),
            color: "success",
          });
        }
        this.formDialog.show = false;
        await this.fetchTemplates();
      } catch (error) {
        this.formDialog.error = this.extractError(
          error,
          "ncrm.integrations.telegram.templates.errors.save"
        );
      } finally {
        this.formDialog.saving = false;
      }
    },
    openDelete(row) {
      this.deleteDialog = { show: true, row, confirm: "", deleting: false };
    },
    closeDelete() {
      this.deleteDialog.show = false;
    },
    async confirmDelete() {
      if (this.deleteDialog.confirm !== "DELETE") return;
      this.deleteDialog.deleting = true;
      try {
        await axios.delete(`/integrations/telegram/templates/${this.deleteDialog.row.id}`);
        this.$emit("notify", {
          message: this.$t("ncrm.integrations.telegram.templates.snackbar.deleted"),
          color: "success",
        });
        this.deleteDialog.show = false;
        await this.fetchTemplates();
      } catch (error) {
        this.$emit("notify", {
          message: this.extractError(error, "ncrm.integrations.telegram.templates.errors.delete"),
          color: "error",
        });
      } finally {
        this.deleteDialog.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.telegram-template-body-snippet {
  font-family: var(--app-font-family-mono);
  font-size: 0.78rem;
  color: var(--app-text-default);
  opacity: 0.85;
}
</style>
